import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { RefundQuery, StoreQuery, ReturnQuery, CacheQuery, RefundRestQuery } from '../../../services/query';
import Helper from '../../../utils/helper';
import {
    RefundTab,
    addPrepareRefundTransferTab,
    buildTabListForValidation,
    canValidateRefundMethod,
    fetchRefundMethodValidation,
    getUniquePayments,
    hasOpenRefundDetailStatus,
    shouldShowPrepareRefundTab,
    showSwalError,
} from './RefundV2.helpers';

const createInitialTabList = (t: (key: string) => string): RefundTab[] => [
    { id: 1, name: t('refund.customerPayments') },
    { id: 3, name: t('refund.refundHistory') },
];

export const useRefundV2 = (props: any) => {
    const query = new URLSearchParams(useLocation().search);
    const idfromUrl = query.get('id');
    const refundId = props.location.state?.id ? props.location.state?.id : idfromUrl;
    const { t } = useTranslation();

    const [paymentListInfo, setPaymentListInfo] = useState<any>();
    const [uniquePaymentListInfo, setUniquePaymentListInfo] = useState<any>();
    const [payBackListInfo, setPayBackListInfo] = useState<any>();
    const [getRefundDetails, setGetRefundDetails] = useState<any>();
    const [irsaliyeFaturaSeriNo, setIrsaliyeFaturaSeriNo] = useState<any>();
    const [isMpApprover, setIsMpApprover] = useState<any>();
    const [expanded, setExpanded] = useState<string | false>(false);
    const [customerId, setCustomerId] = useState<any>();
    const [customerChoiceBankAccountId, setCustomerChoiceBankAccountId] = useState<any>();
    const [shippingListId, setShippingListId] = useState<any>();
    const [tabIndex, setTabIndex] = useState<any>(1);
    const [orderId, setOrderId] = useState<any>(0);
    const [returnId, setReturnId] = useState<any>();
    const [loading, setLoading] = useState(false);
    const [paymentListFinished, setPaymentListFinished] = useState(false);
    const [returnDatas, setReturnData] = useState<any>();
    const [waitingRefundVisible, setWaitingRefundVisible] = useState(false);
    const [orderDate, setOrderDate] = useState<any>(null);
    const [isMarketplacePayment, setIsMarketPlacePayment] = useState(false);
    const [documentHeaderOfInvoice, setDocumentHeaderOfInvoice] = useState<any>();
    const [hasPaymentConversionCloseRefundRole, setHasPaymentConversionCloseRefundRole] = useState(false);
    const [tabList, setTabList] = useState<RefundTab[]>(() => createInitialTabList(t));

    const applyTabListValidation = (isValidFromApi: boolean | null, hasRefundDetailStatusOne: boolean) => {
        setTabList((prevTabList) => {
            const { tabList: nextTabList, activeTabIndex } = buildTabListForValidation(
                prevTabList,
                isValidFromApi,
                hasRefundDetailStatusOne,
                {
                    finishedRefunds: t('refund.finishedRefunds'),
                    waitingRefunds: t('refund.waitingRefunds'),
                    paymentConversion: t('paymentConversion.title'),
                }
            );

            if (activeTabIndex !== undefined) {
                setTabIndex(activeTabIndex);
            }

            return nextTabList;
        });
    };

    const checkRefundMethodValidation = async (refundEntity: any, hasRefundDetailStatusOne: boolean) => {
        if (!canValidateRefundMethod(paymentListInfo, refundEntity)) {
            applyTabListValidation(null, hasRefundDetailStatusOne);
            return;
        }

        const isValid = await fetchRefundMethodValidation(refundEntity, paymentListInfo);
        applyTabListValidation(isValid, hasRefundDetailStatusOne);
    };

    const paymentList = () => {
        RefundRestQuery.PaymentList({ refundId: refundId })
            .then((res: any) => {
                if (res) {
                    setOrderDate(res.items?.[0]?.createDate ?? null);
                    setPaymentListInfo(res);
                    setOrderId(res.items && res.items.length > 0 && res.items[0].orderId);
                    setUniquePaymentListInfo(getUniquePayments(res.items));
                } else {
                    showSwalError(t('refund.noPaymentFound'));
                }
                setPaymentListFinished(true);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const payBackList = () => {
        RefundRestQuery.PayBackList({ refundId: refundId }).then((res: any) => {
            if (res) {
                setPayBackListInfo(res);
            } else {
                showSwalError(t('refund.noPaymentFound'));
            }
        });
    };

    const getRefundDetailsRequest = () => {
        setLoading(true);
        const params: any = {
            refundId: refundId,
            orderDate: orderDate ? orderDate : null,
        };

        RefundRestQuery.GetRefundDetails(params)
            .then((res: any) => {
                if (res && res.entity) {
                    setGetRefundDetails(res);
                    setCustomerId(res.entity.customerId);
                    if (res.entity.orderId !== 0) {
                        setOrderId(res.entity.orderId);
                    }
                    if (res.entity.refundReasonId === 2) {
                        setReturnId(res.entity.referenceId);
                    }
                    setShippingListId(res.entity.shippingListId);
                    if (res.entity.refundDetails[0].customerChoiceBankAccountId) {
                        setCustomerChoiceBankAccountId(res.entity.refundDetails[0].customerChoiceBankAccountId);
                    }

                    const hasRefundDetailStatusOne = hasOpenRefundDetailStatus(res.entity.refundDetails);
                    setWaitingRefundVisible(hasRefundDetailStatusOne);
                    checkRefundMethodValidation(res.entity, hasRefundDetailStatusOne);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const getReturnDetailByReturnId = () => {
        ReturnQuery.GetReturnDetailByReturnId({
            returnId: returnId,
            customerId: customerId,
        }).then((res: any) => {
            if (!res.data) {
                return;
            }
            setReturnData(res.data);
            if (res.data.productDetailModels) {
                setShippingListId(res.data.productDetailModels[0].shippingListId);
            }

            if (shouldShowPrepareRefundTab(res.data, isMpApprover)) {
                setTabList((currentTabList) =>
                    addPrepareRefundTransferTab(currentTabList, t('refund.prepareRefundTransfer'))
                );
            }

            if (res.data.isMarketPlaceReturn) {
                setIsMarketPlacePayment(true);
            }
        });
    };

    const getDocumentHeaderOfInvoiceByOrderId = (currentOrderId: any) => {
        StoreQuery.GetDocumentHeaderOfInvoiceByOrderId({ orderId: currentOrderId }).then((res: any) => {
            if (res) {
                setDocumentHeaderOfInvoice(res.data);
            }
        });
    };

    const getDocumentNo = () => {
        RefundQuery.GetDocumentHeaderOfSellerReturnDispatchList({
            returnId: returnId,
            ShippingListId: shippingListId,
        }).then((res: any) => {
            if (res.data.result) {
                setIrsaliyeFaturaSeriNo(res.data.result.formSerie + res.data.result.formOrderNo);
            }
        });
    };

    useEffect(() => {
        if (orderId && orderId !== 0) {
            getDocumentHeaderOfInvoiceByOrderId(orderId);
        }
    }, [orderId]);

    useEffect(() => {
        if (shippingListId && Helper.isStoreEmployeeForFrontend() && isMarketplacePayment) {
            getDocumentNo();
        }
    }, [shippingListId, isMarketplacePayment]);

    useEffect(() => {
        if (refundId) {
            paymentList();
            payBackList();
        }
    }, [refundId]);

    useEffect(() => {
        if (returnId) {
            getReturnDetailByReturnId();
        }
    }, [returnId]);

    useEffect(() => {
        if (paymentListFinished) {
            getRefundDetailsRequest();
        }
    }, [paymentListFinished]);

    useEffect(() => {
        CacheQuery.GetUSerClaims().then((res: any) => {
            setIsMpApprover(res.includes('backoffice_mpReturnApprover'));
        });
    }, []);

    useEffect(() => {
        RefundRestQuery.CheckPaymentConversionCloseRefundRole()
            .then((res: any) => {
                setHasPaymentConversionCloseRefundRole(res?.data);
            })
            .catch(() => setHasPaymentConversionCloseRefundRole(false));
    }, []);

    const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const onChangeTabs = (_event: any, activeTabIndex: any): void => {
        setTabIndex(activeTabIndex);
    };

    return {
        refundId,
        paymentListInfo,
        uniquePaymentListInfo,
        payBackListInfo,
        getRefundDetails,
        irsaliyeFaturaSeriNo,
        expanded,
        customerId,
        customerChoiceBankAccountId,
        shippingListId,
        tabIndex,
        orderId,
        returnId,
        loading,
        returnDatas,
        waitingRefundVisible,
        orderDate,
        documentHeaderOfInvoice,
        hasPaymentConversionCloseRefundRole,
        tabList,
        handleAccordionChange,
        onChangeTabs,
    };
};
